/**
 * Insignia / chip de la librería de UI (Tailwind, estilos del proyecto).
 *
 * Presentacional y sin lógica: un `<span>` redondeado (pill) con la paleta azul
 * del proyecto (paridad con el SSR). Es la pieza estándar para categorías y
 * estados (sustituye a los chips ad-hoc de las pantallas en los barridos).
 *
 * Tonos:
 * - `accent`  → sólido azul del proyecto (default).
 * - `outline` → borde suave sobre fondo transparente.
 * - `muted`   → gris muy claro con borde suave.
 * - estados `success | warning | danger | info` → fondo suave + texto del estado.
 */
import { type HTMLAttributes } from "react";

import { cn } from "./cn";

export type BadgeTone =
  | "accent"
  | "outline"
  | "muted"
  | "success"
  | "warning"
  | "danger"
  | "info";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
}

const TONES: Record<BadgeTone, string> = {
  accent: "bg-brand text-white",
  outline: "border border-slate-300 text-slate-700",
  muted: "bg-slate-100 text-slate-600 border border-slate-200",
  success: "bg-emerald-50 text-emerald-700",
  warning: "bg-amber-50 text-amber-700",
  danger: "bg-red-50 text-red-700",
  info: "bg-cyan-50 text-cyan-700",
};

export function Badge({ tone = "accent", className, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium leading-none",
        TONES[tone],
        className,
      )}
      {...props}
    />
  );
}
